import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
// Replace standard Image with ImageResize
import ImageResize from 'tiptap-extension-resize-image';
import TextAlign from '@tiptap/extension-text-align';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Link from '@tiptap/extension-link';

import DOMPurify from 'dompurify';
import { Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, Trash2 } from 'lucide-react';

import { Toolbar } from './Toolbar';
import { LinkModal } from './LinkModal';
import './Editor.css';

// DOMPurify configuration: updated to allow width, height, and inline styles for resized images
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        'p', 'h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 's',
        'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
        'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'br', 'hr'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'style'],
    FORBID_TAGS: ['script'],
    FORBID_ATTR: ['id']
};

export const isEmptyHtml = (html) => {
    if (!html) return true;

    const text = html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;|&#xA0;/gi, ' ')
        .trim();

    return !text && !/<(?:img|table|hr)\b/i.test(html);
};

export default function BlogEditor({
    value = '',
    onChange,
    placeholder = 'Write your blog content…',
    minHeight = 300,
    hasError = false,
    readOnly = false
}) {
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [slashMenuOpen, setSlashMenuOpen] = useState(false);
    const [slashPos, setSlashPos] = useState({ top: 0, left: 0 });
    const editorRef = useRef(null);

    const editor = useEditor({
        editable: !readOnly,
        content: value,
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3, 4] },
            }),
            Underline,
            // Configured image extension with resize capabilities
            ImageResize.configure({
                allowBase64: true,
                inline: false,
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            CharacterCount,
            Placeholder.configure({
                placeholder,
            }),
            Table.configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'tiptap-table',
                },
            }),
            TableRow,
            TableHeader,
            TableCell,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    rel: 'noopener noreferrer',
                    target: '_blank',
                },
            }),
        ],
        onUpdate: ({ editor }) => {
            const rawHtml = editor.getHTML();
            const sanitizedHtml = DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG);
            if (onChange) {
                onChange(sanitizedHtml);
            }
        },
        editorProps: {
            handleDrop: (view, event, slice, moved) => {
                if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
                    const file = event.dataTransfer.files[0];
                    if (file.type.startsWith('image/')) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            const { schema } = view.state;
                            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                            const node = schema.nodes.image.create({ src: e.target.result });
                            const transaction = view.state.tr.insert(coordinates.pos, node);
                            view.dispatch(transaction);
                        };
                        reader.readAsDataURL(file);
                        return true;
                    }
                }
                return false;
            },
            transformPastedHTML(html) {
                return DOMPurify.sanitize(html, SANITIZE_CONFIG);
            },
        },
    });

    useEffect(() => {
        if (editor && !editor.isFocused && value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);
        }
    }, [value, editor]);

    const handleImageUpload = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    editor?.chain().focus().setImage({ src: event.target.result }).run();
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    }, [editor]);

    const handleSaveLink = (url) => {
        if (!url) {
            editor?.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const handleRemoveLink = () => {
        editor?.chain().focus().unsetLink().run();
    };

    const handleKeyDown = (e) => {
        if (e.key === '/') {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0).cloneRange();
                const rect = range.getBoundingClientRect();
                const parentRect = editorRef.current.getBoundingClientRect();

                setSlashPos({
                    top: rect.bottom - parentRect.top + 6,
                    left: rect.left - parentRect.left,
                });
                setSlashMenuOpen(true);
            }
        } else if (slashMenuOpen && (e.key === 'Escape' || e.key === ' ')) {
            setSlashMenuOpen(false);
        }
    };

    const executeSlashCommand = (action) => {
        editor?.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
        action();
        setSlashMenuOpen(false);
    };

    if (!editor) return null;

    return (
        <div
            ref={editorRef}
            className={`editor-container ${hasError ? 'has-error' : ''}`}
            onKeyDown={handleKeyDown}
        >
            {!readOnly && (
                <Toolbar
                    editor={editor}
                    onOpenLinkModal={() => setIsLinkModalOpen(true)}
                    onUploadImage={handleImageUpload}
                />
            )}

            {editor && !readOnly && (
                <BubbleMenu editor={editor} tippyOptions={{ duration: 150 }} className="bubble-menu">
                    <button
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={`toolbar-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
                    >
                        <Bold size={14} />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={`toolbar-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
                    >
                        <Italic size={14} />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        className={`toolbar-btn ${editor.isActive('underline') ? 'is-active' : ''}`}
                    >
                        <UnderlineIcon size={14} />
                    </button>
                    <button
                        onClick={() => setIsLinkModalOpen(true)}
                        className={`toolbar-btn ${editor.isActive('link') ? 'is-active' : ''}`}
                    >
                        <LinkIcon size={14} />
                    </button>
                </BubbleMenu>
            )}

            <div style={{ minHeight }}>
                <EditorContent editor={editor} />
            </div>

            {slashMenuOpen && !readOnly && (
                <div className="slash-menu" style={{ top: slashPos.top, left: slashPos.left }}>
                    <button
                        className="slash-item"
                        onClick={() => executeSlashCommand(() => editor.chain().focus().toggleHeading({ level: 2 }).run())}
                    >
                        <span className="slash-title">Heading 2</span>
                        <span className="slash-desc">Medium section heading</span>
                    </button>
                    <button
                        className="slash-item"
                        onClick={() => executeSlashCommand(() => editor.chain().focus().toggleBlockquote().run())}
                    >
                        <span className="slash-title">Quote</span>
                        <span className="slash-desc">Highlight important text</span>
                    </button>
                    <button
                        className="slash-item"
                        onClick={() => executeSlashCommand(() => handleImageUpload())}
                    >
                        <span className="slash-title">Image</span>
                        <span className="slash-desc">Upload or embed an image</span>
                    </button>
                    <button
                        className="slash-item"
                        onClick={() => executeSlashCommand(() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}
                    >
                        <span className="slash-title">Table</span>
                        <span className="slash-desc">Insert a data table</span>
                    </button>
                    <button
                        className="slash-item"
                        onClick={() => executeSlashCommand(() => editor.chain().focus().toggleCodeBlock().run())}
                    >
                        <span className="slash-title">Code Block</span>
                        <span className="slash-desc">Format code snippets</span>
                    </button>
                </div>
            )}

            {editor.isActive('table') && !readOnly && (
                <div className="table-controls">
                    <span>Table Actions:</span>
                    <button onClick={() => editor.chain().focus().addColumnBefore().run()}>+ Col Left</button>
                    <button onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col Right</button>
                    <button onClick={() => editor.chain().focus().deleteColumn().run()}>- Delete Col</button>
                    <button onClick={() => editor.chain().focus().addRowBefore().run()}>+ Row Above</button>
                    <button onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row Below</button>
                    <button onClick={() => editor.chain().focus().deleteRow().run()}>- Delete Row</button>
                    <button className="danger" onClick={() => editor.chain().focus().deleteTable().run()}>
                        <Trash2 size={12} style={{ display: 'inline', marginRight: '3px' }} /> Delete Table
                    </button>
                </div>
            )}

            <div className="editor-footer">
                <span>{editor.storage.characterCount.words()} words</span>
                <span>{editor.storage.characterCount.characters()} characters</span>
            </div>

            <LinkModal
                isOpen={isLinkModalOpen}
                onClose={() => setIsLinkModalOpen(false)}
                onSave={handleSaveLink}
                onRemove={handleRemoveLink}
                initialUrl={editor.getAttributes('link').href || ''}
            />
        </div>
    );
}